import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { BRAND_CONFIG, type BrandConfig } from 'constants/branding';
import { mergeBrand, pathIsAllowed } from 'utils/portalBrand';
import { portalProfileService, type PortalProfile } from 'api/services/portalProfile.service';
import { useAuth } from 'hooks/useAuth';

/**
 * The service-provider branding layer.
 *
 * <h3>The cascade</h3>
 * <pre>
 *   1. service provider profile   (this file — data, per reseller)
 *   2. hostname                   (BRAND_CONFIG, resolved at import)
 *   3. build-time default
 * </pre>
 *
 * A reseller is a database row, not a deploy. Layer 1 only ever OVERRIDES the
 * identity fields it actually carries; everything else — gradients, login copy,
 * terms — comes from the layer beneath, so onboarding a reseller does not mean
 * specifying a colour scheme.
 *
 * <h3>Why the login page is not covered by this</h3>
 * A provider profile can only be known AFTER authentication — before sign-in
 * there is no organization and so no provider. The login screen therefore keeps
 * resolving from the hostname, which is why the hostname layer has to stay and
 * why a reseller wanting branded sign-in needs a hostname pattern too. Login and
 * ResetPassword deliberately read BRAND_CONFIG directly.
 *
 * <h3>Why the resolved brand is cached</h3>
 * The profile arrives over the network after the first paint. Without a cache,
 * every reload shows the underlying brand for a moment and then swaps — a
 * customer sees somebody else's name flash on their own portal. The cache is
 * keyed by organization so switching business cannot show the previous one's
 * brand.
 */

interface PortalProfileContextValue {
  /** The brand after the provider profile has been merged over the hostname one. */
  brand: BrandConfig;
  /** True when a reseller profile is in force. */
  applies: boolean;
  /** Paths this portal may use; empty means no restriction. */
  allowedPaths: string[];
  /**
   * Whether a nav path is outside this portal's allowance.
   *
   * Always false when there is no allowlist — the absence of a profile must
   * never narrow anybody's portal.
   */
  isLocked: (path: string) => boolean;
  loading: boolean;
}

const PortalProfileContext = createContext<PortalProfileContextValue | null>(null);

const cacheKey = (orgId: string) => `portalProfile:${orgId}`;

export const PortalProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentOrganization, isAuthenticated } = useAuth();
  const orgId = currentOrganization?.id ?? null;

  // Seeded from the cache so the first paint after a reload is already correct.
  const [profile, setProfile] = useState<PortalProfile | null>(() => {
    try {
      const stored = orgId ? localStorage.getItem(cacheKey(orgId)) : null;
      return stored ? (JSON.parse(stored) as PortalProfile) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !orgId) { setProfile(null); return; }

    // Show the cached answer immediately, then confirm it. The organization can
    // change without a reload via the business switcher, so this reads the cache
    // again here rather than relying on the initialiser, which runs once.
    try {
      const stored = localStorage.getItem(cacheKey(orgId));
      setProfile(stored ? (JSON.parse(stored) as PortalProfile) : null);
    } catch { /* a corrupt cache is not worth failing over */ }

    let alive = true;
    setLoading(true);
    (async () => {
      const res = await portalProfileService.mine();
      if (!alive) return;
      if (res.success && res.data) {
        setProfile(res.data);
        try { localStorage.setItem(cacheKey(orgId), JSON.stringify(res.data)); } catch { /* private mode */ }
      }
      // A failed call leaves the cached answer in place. Dropping to the
      // underlying brand because one request failed would show a reseller's
      // customer somebody else's name.
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [isAuthenticated, orgId]);

  const allowedPaths = useMemo(
    () => (profile?.applies ? profile.allowedPaths ?? [] : []),
    [profile],
  );

  const isLocked = useCallback(
    (path: string) => !pathIsAllowed(path, allowedPaths),
    [allowedPaths],
  );

  const value = useMemo<PortalProfileContextValue>(() => ({
    brand: mergeBrand(BRAND_CONFIG, profile),
    applies: profile?.applies === true,
    allowedPaths,
    isLocked,
    loading,
  }), [profile, allowedPaths, isLocked, loading]);

  return (
    <PortalProfileContext.Provider value={value}>{children}</PortalProfileContext.Provider>
  );
};

/**
 * The portal's brand and allowance.
 *
 * Safe outside the provider — falls back to the hostname brand with no
 * restriction, so a component rendered on the login screen still works.
 */
export function usePortalProfile(): PortalProfileContextValue {
  const ctx = useContext(PortalProfileContext);
  if (ctx) return ctx;
  return {
    brand: BRAND_CONFIG,
    applies: false,
    allowedPaths: [],
    isLocked: () => false,
    loading: false,
  };
}

